import React, { useState } from "react";
import {
  LockClosedIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ShieldExclamationIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import SEO from "../components/SEO";
import api from "../utils/api";
import { downloadBase64File, getPasswordStrength } from "../utils/helpers";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { certificateName } from "../../shared/utils/certificateNaming";

function PFXConverter() {
  const [certificate, setCertificate] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [password, setPassword] = useState("");
  const [encryption, setEncryption] = useState("3des-sha1");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // 2026-07-04 tools-wide overhaul (operator-approved), audit T2 #7 +
  // the window.confirm replacement : the no-password confirmation is
  // now an inline, style-guide-compliant Alert with explicit buttons
  // instead of a browser-native window.confirm dialog (which cannot
  // be styled, breaks the page's visual language, and shouted an
  // UPPERCASE question at the customer).
  const [showNoPasswordConfirm, setShowNoPasswordConfirm] = useState(false);
  const resultsRef = useScrollToResults(result);

  const passwordStrength = getPasswordStrength(password);

  /**
   * Run the actual conversion call. Split out of handleSubmit so the
   * inline no-password confirmation button can invoke the same code
   * path after the customer explicitly confirms.
   */
  const performConversion = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.convertToPFX(
        certificate,
        privateKey,
        password || null,
        encryption
      );
      setResult(response.data);
    } catch (err) {
      setError(err.message || "The PFX file could not be created.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!certificate.trim() || !privateKey.trim()) {
      // 2026-07-04 tools-wide overhaul, audit T2 #5 : customer errors
      // say "SSL Certificate" in full, never the bare word.
      setError("Please provide both the SSL Certificate and the private key.");
      return;
    }

    // Password is optional, but creating an unprotected PFX file is
    // risky enough to warrant an explicit inline confirmation first.
    if (!password) {
      setShowNoPasswordConfirm(true);
      return;
    }

    await performConversion();
  };

  /** Customer explicitly confirmed the password-free PFX file. */
  const handleConfirmNoPassword = async () => {
    setShowNoPasswordConfirm(false);
    await performConversion();
  };

  const handleDownload = () => {
    if (!result || !result.pfx) {
      setError("The PFX content is missing or not valid.");
      return;
    }
    const pfxB64 =
      typeof result.pfx === "string" ? result.pfx : String(result.pfx || "");
    if (!pfxB64 || pfxB64.length < 32) {
      setError("The PFX content is missing or not valid.");
      return;
    }
    const name =
      result.filename && typeof result.filename === "string"
        ? result.filename
        : "certificate.pfx";
    downloadBase64File(
      pfxB64,
      name.endsWith(".pfx") ? name : `${name}.pfx`,
      "application/x-pkcs12"
    );
  };

  const handleReset = () => {
    setCertificate("");
    setPrivateKey("");
    setPassword("");
    setResult(null);
    setError(null);
    setShowNoPasswordConfirm(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/*
        2026-07-04 tools-wide overhaul (operator-approved) : keywords
        dropped the style-guide-violating "SSL to PFX" ("SSL" alone
        never stands in for "SSL Certificate"), "private keys" follows
        the lowercase-in-sentences ruling (2026-08-28 : one casing for
        descriptive nouns in prose), and the canonical URL is added
        (the shared SEO component only emits the canonical link when
        the prop is provided).
      */}
      <SEO
        title="PFX/P12 Converter - Convert SSL Certificate to PFX Format"
        description="Convert SSL Certificates and private keys to PFX/PKCS12 format online. Create password-protected PFX files for Windows IIS, Exchange, and other servers. Free PFX converter tool."
        keywords="PFX converter, P12 converter, PKCS12 converter, SSL Certificate to PFX, create PFX file, PFX generator, IIS SSL Certificate converter, PEM to PFX"
        canonicalUrl="https://tools.trustico.com/pfx"
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          SSL Certificate to PFX Converter
        </h1>
        <p className="text-body">
          Combine an SSL Certificate and its private key into a single
          password-protected PFX (PKCS#12) file, sometimes called a P12 file.
          Windows servers such as Internet Information Services (IIS) and
          Microsoft Exchange, along with many appliances, expect this format
          instead of separate PEM files. Paste both parts, set a password and
          choose the encryption, then download the finished file ready for
          import.
        </p>
      </div>

      <SecurityNotice />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Certificate and Key Inputs */}
        <div className="grid grid-cols-1 gap-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              SSL Certificate
            </h2>
            <p className="text-body mb-4">
              Paste your SSL Certificate in PEM format below. This is the SSL
              Certificate you received from your Certificate Authority (CA)
              after your CSR was approved. Ensure it is in PEM format with the
              proper BEGIN and END markers.
            </p>
            <textarea
              value={certificate}
              onChange={(e) => setCertificate(e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              className="textarea certificate-textarea"
              rows={12}
            />
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Private Key
            </h2>
            <p className="text-body mb-4">
              Paste your private key in PEM format below. This is the key that
              was generated alongside your CSR. Ensure this key matches the SSL
              Certificate above. The private key must be in PEM format.
            </p>
            <textarea
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
              className="textarea certificate-textarea"
              rows={12}
            />
          </div>
        </div>

        {/* Password Input */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-heading mb-2">
            PFX Password (Optional)
          </h2>
          <p className="text-body mb-4">
            Set a strong password to protect your PFX file. While optional, we
            highly recommend using a password to secure your SSL Certificate and
            private key bundle. This password will be required when importing
            the PFX file into your server or application.
          </p>
          <div className="max-w-md">
            <div className="relative">
              {/* 2026-07-04 operator consistency directive :
                  placeholders are realistic example values, never
                  instructional prose (was "Enter a strong password"). */}
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // Typing a password makes a pending no-password
                  // confirmation stale ; dismiss it so the customer
                  // never confirms an outdated question.
                  if (showNoPasswordConfirm) {
                    setShowNoPasswordConfirm(false);
                  }
                }}
                placeholder="S3cure!Passw0rd"
                className="input pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted hover:text-body"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {password && (
              <div className="mt-2">
                <div className="flex items-center">
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 dark:inset-ring-1 dark:inset-ring-control rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        passwordStrength.color === "red"
                          ? "bg-red-500"
                          : passwordStrength.color === "orange"
                            ? "bg-orange-500"
                            : passwordStrength.color === "yellow"
                              ? "bg-yellow-500"
                              : "bg-green-500"
                      }`}
                      style={{
                        width: `${(passwordStrength.score / 6) * 100}%`,
                      }}
                    />
                  </div>
                  <span
                    className={`ml-3 font-medium ${
                      passwordStrength.color === "red"
                        ? "text-red-600 dark:text-red-300"
                        : passwordStrength.color === "orange"
                          ? "text-orange-700 dark:text-orange-400"
                          : passwordStrength.color === "yellow"
                            ? "text-yellow-700 dark:text-yellow-400"
                            : "text-green-700 dark:text-green-400"
                    }`}
                  >
                    {passwordStrength.text}
                  </span>
                </div>
                <p className="mt-1 text-body">
                  Use at Least 8 Characters with Uppercase, Lowercase, Numbers,
                  and Special Characters
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Encryption Selection */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-heading mb-2">
            PFX Encryption
          </h2>
          <p className="text-body mb-4">
            Choose encryption for password-protected PFX files.{" "}
            <strong>TripleDES-SHA1 (Legacy Compatibility)</strong> is
            recommended for older Windows systems. AES256-SHA256 (Modern
            Compatibility) offers stronger security but may fail to import on
            legacy systems.
          </p>
          <div className="max-w-md">
            <select
              className="input"
              value={encryption}
              onChange={(e) => setEncryption(e.target.value)}
            >
              <option value="3des-sha1">
                TripleDES-SHA1 (Legacy Compatibility)
              </option>
              <option value="aes256-sha256">
                AES256-SHA256 (Modern Compatibility)
              </option>
            </select>
          </div>
        </div>

        {/*
          Inline no-password confirmation (replaces window.confirm,
          2026-07-04 tools-wide overhaul, operator-approved). A
          warning notice per the audit T2 #7 ruling : Title-Case
          title, sentence-case body, explicit buttons. Rendered above
          the action row so it is visible next to the button that
          triggered it.
        */}
        {showNoPasswordConfirm && (
          <Alert
            type="warning"
            title="Password Protection Recommended"
            onClose={() => setShowNoPasswordConfirm(false)}
          >
            <p className="mb-4">
              You are about to create a PFX file without password protection.
              Anyone who obtains the file can install your SSL Certificate and
              private key, so we strongly recommend setting a password above
              before continuing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <ActionButton
                type="button"
                onClick={handleConfirmNoPassword}
                variant="primary"
                icon={ShieldExclamationIcon}
              >
                Continue Without Password
              </ActionButton>
              <ActionButton
                type="button"
                onClick={() => setShowNoPasswordConfirm(false)}
                variant="outline"
                icon={XMarkIcon}
              >
                Cancel
              </ActionButton>
            </div>
          </Alert>
        )}

        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            <ActionButton
              type="submit"
              variant="primary"
              disabled={loading || !certificate.trim() || !privateKey.trim()}
              loading={loading}
              loadingText="Converting..."
              icon={LockClosedIcon}
            >
              Convert to PFX
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleReset}
              variant="outline"
              icon={XMarkIcon}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data"). */}
              Reset
            </ActionButton>
          </div>
        </div>
      </form>

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <div ref={resultsRef} className="mt-8 space-y-6 animate-fade-in">
          {/*
            2026-07-04 tools-wide overhaul (operator-approved), audit
            T2 #7 : success is a status notice, not error text, so the
            heading and the no-password variant drop their blanket
            UPPERCASE for Title Case. The encryption suffix stays
            uppercase because 3DES-SHA1 / AES256-SHA256 are technical
            cipher identifiers, not prose.

            2026-07-04 operator consistency directive, heading-size
            sweep decision : the success heading below keeps text-2xl
            font-bold DELIBERATELY. It is a result-status banner
            (centered icon + colored verdict), not a section heading,
            and it matches the identical banner treatment in
            CertificateMatcher.jsx (the only other status banner in the
            suite). Section headings follow the text-lg
            font-semibold reference size.
          */}
          <div className="card p-8 text-center bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800/50">
            <CheckCircleIcon className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-2">
              PFX File Generated Successfully
            </h2>
            <p className="text-green-700 dark:text-green-300 mb-6">
              {result.passwordProtected
                ? `PFX File Created with Password Protection (${(result.encryption || "").toUpperCase()})`
                : "PFX File Created Without Password Protection"}
            </p>

            <ActionButton
              onClick={handleDownload}
              variant="primary"
              icon={ArrowDownTrayIcon}
              className="mx-auto"
            >
              Download PFX File
            </ActionButton>
          </div>

          {/* Certificate Information - Consistent with Chain Analyzer */}
          {result.certificate && (
            <div className="card p-6 mt-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                SSL Certificate Information
              </h2>
              {/* 2026-07-04 operator consistency directive : every
                  section heading carries an explanatory paragraph (the
                  CSRGenerator.jsx reference idiom). */}
              <p className="text-body mb-4">
                These details describe the SSL Certificate bundled into your PFX
                file, so you can confirm the correct SSL Certificate was
                converted.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  {/* ISSUED TO, AND READ FROM THE SHARED HELPER
                      (operator ruling, 2026-08-21 : "the labels should
                      be updated too. We don't want to have something
                      like this half fixed"). Not every SSL Certificate
                      names a domain - an S/MIME certificate identifies
                      a person by e-mail address and carries no common
                      name at all, so this card read "N/A" for a
                      certificate whose identity was sitting unread in
                      its Subject Alternative Names. The same fix the
                      Chain Analyzer took, on the same helper.

                      THE PLAIN NAME, WITHOUT THE "+ N More SANs" COUNT
                      (operator ruling, 2026-08-21). The Chain Analyzer
                      earns that suffix because it carries a Subject
                      Alternative Names section listing every one of
                      them, so the count points somewhere. This page has
                      no such section, so the suffix would leave a
                      customer with a number, an abbreviation nobody
                      introduced, and nowhere to go. */}
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Issued To
                  </span>
                  <p className="text-heading font-medium mt-1">
                    {certificateName(result.certificate) || "N/A"}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Organization
                  </span>
                  <p className="text-heading mt-1">
                    {result.certificate.subject.organizationName || "N/A"}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Validity Period
                  </span>
                  <p className="text-heading mt-1">
                    {new Date(
                      result.certificate.notBefore
                    ).toLocaleDateString()}{" "}
                    -{" "}
                    {new Date(result.certificate.notAfter).toLocaleDateString()}
                  </p>
                  <p
                    className={`font-medium mt-1 ${result.certificate.daysUntilExpiry < 30 ? "text-red-600 dark:text-red-300" : "text-green-700 dark:text-green-400"}`}
                  >
                    {result.certificate.daysUntilExpiry < 30
                      ? "EXPIRING SOON"
                      : "VALID"}{" "}
                    ({result.certificate.daysUntilExpiry} Days Remaining)
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    PFX Details
                  </span>
                  <p className="text-heading mt-1">
                    {result.chainLength} SSL{" "}
                    {result.chainLength === 1 ? "Certificate" : "Certificates"}{" "}
                    Included
                  </p>
                  <p className="text-body mt-1">
                    {result.passwordProtected
                      ? "Password Protected"
                      : "No Password"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PFXConverter;
